import { useEffect, useState, useCallback } from "react";
import { Landmark, Download, FileText, ArrowRight } from "lucide-react";
import { api, downloadBlob } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function periodsForYear(freq, year) {
  const p = [];
  const iso = (m, d) => `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  if (freq === "monthly") {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    for (let m = 1; m <= 12; m++)
      p.push({ label: new Date(year, m - 1, 1).toLocaleString("en-CA", { month: "long" }), start: iso(m, 1), end: iso(m, days[m - 1]) });
  } else if (freq === "quarterly") {
    p.push({ label: "Q1 (Jan–Mar)", start: iso(1, 1), end: iso(3, 31) });
    p.push({ label: "Q2 (Apr–Jun)", start: iso(4, 1), end: iso(6, 30) });
    p.push({ label: "Q3 (Jul–Sep)", start: iso(7, 1), end: iso(9, 30) });
    p.push({ label: "Q4 (Oct–Dec)", start: iso(10, 1), end: iso(12, 31) });
  } else {
    p.push({ label: `Full year ${year}`, start: iso(1, 1), end: iso(12, 31) });
  }
  return p;
}

export default function Gst() {
  const { current, currentId } = useCompany();
  const year = new Date().getFullYear();
  const freq = current?.filing_frequency || "quarterly";
  const presets = periodsForYear(freq, year);
  const [presetIdx, setPresetIdx] = useState("0");
  const [start, setStart] = useState(presets[0].start);
  const [end, setEnd] = useState(presets[0].end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState("accrual");

  const fetchSummary = useCallback(
    (s, e) => {
      if (!currentId) return;
      setLoading(true);
      api
        .get("/gst/summary", { params: { start: s, end: e, basis } })
        .then((r) => setData(r.data))
        .catch(() => toast.error("Could not load GST summary"))
        .finally(() => setLoading(false));
    },
    [currentId, basis]
  );

  useEffect(() => {
    const p = periodsForYear(current?.filing_frequency || "quarterly", year);
    setStart(p[0].start);
    setEnd(p[0].end);
    setPresetIdx("0");
    fetchSummary(p[0].start, p[0].end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, current?.filing_frequency, year]);

  useEffect(() => {
    fetchSummary(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basis]);

  const applyPreset = (idx) => {
    setPresetIdx(idx);
    const p = presets[parseInt(idx)];
    setStart(p.start);
    setEnd(p.end);
    fetchSummary(p.start, p.end);
  };

  const exportFile = async (type) => {
    try {
      await downloadBlob(
        `/gst/export/${type}?start=${start}&end=${end}&basis=${basis}`,
        `gst-${start}-to-${end}.${type}`
      );
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const netOwing = data?.net_gst_owing ?? 0;
  const refund = netOwing < 0;

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Alberta GST Return</h1>
        <p className="text-slate-500 text-sm mt-1">
          GST34-style summary · <span className="capitalize">{freq}</span> filing · GST Collected − ITCs = Net Owing
        </p>
      </div>

      <Card className="p-5 rounded-lg border-slate-200 shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Filing period</Label>
            <Select value={presetIdx} onValueChange={applyPreset}>
              <SelectTrigger data-testid="gst-period-select" className="mt-1 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {presets.map((p, i) => (
                  <SelectItem key={i} value={String(i)}>{p.label} {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <div className="mt-1">
              <DatePicker testId="gst-start-picker" value={start} onChange={(d) => { setStart(d); fetchSummary(d, end); }} />
            </div>
          </div>
          <div>
            <Label>To</Label>
            <div className="mt-1">
              <DatePicker testId="gst-end-picker" value={end} onChange={(d) => { setEnd(d); fetchSummary(start, d); }} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="export-csv-btn" onClick={() => exportFile("csv")} className="rounded-md flex-1">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" data-testid="export-pdf-btn" onClick={() => exportFile("pdf")} className="rounded-md flex-1">
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-slate-500">Basis:</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="gst-basis-toggle">
            {["accrual", "cash"].map((b) => (
              <button
                key={b}
                data-testid={`gst-basis-${b}`}
                onClick={() => setBasis(b)}
                className={`px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                  basis === b ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400">
            {basis === "cash" ? "GST recognised when invoices are paid." : "GST recognised when invoices are issued."}
          </span>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading font-semibold text-slate-900">Return Breakdown</h2>
            </div>
            <table className="w-full text-sm" data-testid="gst-breakdown-table">
              <tbody>
                <Line line="101" label="Sales & revenue (invoices, pre-GST)" value={data?.sales_subtotal} />
                <Line line="101b" label="Recurring income (pre-GST)" value={data?.recurring_amount} />
                <Line line="105a" label="GST collected on sales" value={data?.sales_gst} />
                <Line line="105b" label="GST collected on recurring income" value={data?.recurring_gst} />
                <Line line="105" label="Total GST collected" value={data?.gst_collected} bold />
                <Line line="108" label="Input Tax Credits (ITCs)" value={data?.itc} tone="text-blue-600" />
              </tbody>
            </table>
          </Card>
        </div>

        <Card className={`p-6 rounded-lg shadow-sm border ${refund ? "bg-emerald-600 border-emerald-600" : "bg-slate-900 border-slate-900"} text-white flex flex-col justify-center`}>
          <div className="flex items-center gap-2 text-slate-200">
            <Landmark className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wide">Line 109 · {refund ? "Net Refund" : "Net GST Owing"}</span>
          </div>
          <div className="font-heading text-4xl font-bold tabular-nums mt-3" data-testid="gst-net-owing">
            {loading ? "…" : money(Math.abs(netOwing))}
          </div>
          <div className="mt-4 text-sm text-slate-200 flex items-center gap-2 flex-wrap tabular-nums">
            <span>{money(data?.gst_collected)}</span>
            <span className="opacity-60">collected</span>
            <span className="opacity-60">−</span>
            <span>{money(data?.itc)}</span>
            <span className="opacity-60">ITCs</span>
          </div>
          <div className="mt-4 text-xs text-slate-300">
            {refund ? "You are owed a refund for this period." : "Amount payable to CRA for this period."}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Line({ line, label, value, bold, tone }) {
  return (
    <tr className={`border-b border-slate-50 ${bold ? "bg-slate-50" : ""}`}>
      <td className="px-5 py-3 text-xs text-slate-400 w-16 tabular-nums">{line}</td>
      <td className={`px-2 py-3 ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</td>
      <td className={`px-5 py-3 text-right tabular-nums ${bold ? "font-bold text-slate-900" : tone || "text-slate-900"}`}>
        {money(value)}
      </td>
    </tr>
  );
}
